/**
 * Download helpers for optimized file downloads across different devices
 */

import { accessService } from '../services/access.service';
import { getDownloadStrategy, getDownloadDelay, detectDevice } from './deviceDetection';
import { Photo } from '../types';

export interface DownloadProgress {
  completed: number;
  total: number;
  currentFile?: string;
  percentage: number;
}

export type DownloadProgressCallback = (progress: DownloadProgress) => void;

/**
 * Downloads a single photo with proper filename
 */
async function downloadSinglePhoto(
  uuid: string, 
  photo: Photo, 
  tagName: string
): Promise<void> {
  try {
    const blob = await accessService.downloadPhoto(uuid, photo.id);
    
    const sanitizedTagName = tagName.replace(/\s+/g, '_');
    const sanitizedPhotoName = photo.originalName.replace(/[^a-zA-Z0-9.-]/g, '_');
    
    const filename = `${sanitizedTagName}_${sanitizedPhotoName}`;
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error(`Failed to download photo ${photo.originalName}:`, error);
    throw error;
  }
}

/**
 * Downloads multiple photos individually with progress tracking
 */
async function downloadPhotosIndividually(
  uuid: string,
  photos: Photo[],
  tagName: string,
  onProgress?: DownloadProgressCallback
): Promise<{ successful: number; failed: string[] }> {
  const successful: number[] = [];
  const failed: string[] = [];
  const delay = getDownloadDelay();
  
  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    
    // Update progress
    if (onProgress) {
      onProgress({
        completed: i,
        total: photos.length,
        currentFile: photo.originalName,
        percentage: Math.round((i / photos.length) * 100)
      });
    }
    
    try {
      await downloadSinglePhoto(uuid, photo, tagName);
      successful.push(photo.id);
      
      // Add delay between downloads to avoid overwhelming the browser
      if (i < photos.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`Failed to download ${photo.originalName}:`, error);
      failed.push(photo.originalName);
    }
  }
  
  // Final progress update
  if (onProgress) {
    onProgress({
      completed: photos.length,
      total: photos.length,
      percentage: 100
    });
  }
  
  return { successful: successful.length, failed };
}

/**
 * Downloads photos as a zip file (desktop strategy)
 */
async function downloadPhotosAsZip(
  uuid: string,
  photoIds: number[],
  tagName: string
): Promise<void> {
  const blob = await accessService.downloadMultiplePhotos(uuid, photoIds);
  
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `photos_${tagName.replace(/\s+/g, '_')}_${Date.now()}.zip`;
  a.style.display = 'none';
  
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Smart download function that chooses the best strategy based on device
 */
export async function downloadPhotosOptimized(
  uuid: string,
  photos: Photo[],
  tagName: string,
  onProgress?: DownloadProgressCallback
): Promise<{ successful: number; failed: string[]; strategy: string }> {
  const strategy = getDownloadStrategy();
  const device = detectDevice();
  
  console.log(`Using download strategy: ${strategy} for device:`, {
    isMobile: device.isMobile,
    isTablet: device.isTablet,
    isDesktop: device.isDesktop,
    screenWidth: device.screenSize.width
  });
  
  try {
    if (strategy === 'zip') {
      // Desktop zip download
      await downloadPhotosAsZip(uuid, photos.map(p => p.id), tagName);
      return { 
        successful: photos.length, 
        failed: [], 
        strategy: 'zip' 
      };
    } else {
      // Individual download for mobile/tablet
      const result = await downloadPhotosIndividually(uuid, photos, tagName, onProgress);
      return { 
        ...result, 
        strategy: strategy === 'individual' ? 'individual' : 'sequential' 
      };
    }
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
}

/**
 * Estimates download time based on device and number of photos
 */
export function estimateDownloadTime(photoCount: number): string {
  const device = detectDevice();
  const baseTimePerPhoto = device.isMobile ? 2 : device.isTablet ? 1.5 : 0.5; // seconds
  const delay = getDownloadDelay() / 1000; // convert to seconds
  
  const totalTime = (photoCount * baseTimePerPhoto) + ((photoCount - 1) * delay);
  
  if (totalTime < 60) {
    return `~${Math.ceil(totalTime)} secondes`;
  } else {
    const minutes = Math.ceil(totalTime / 60);
    return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
}

/**
 * Gets a user-friendly description of the download strategy
 */
export function getDownloadStrategyDescription(): string {
  const strategy = getDownloadStrategy();
  
  switch (strategy) {
    case 'zip':
      return 'Téléchargement en archive ZIP (recommandé pour ordinateur)';
    case 'individual':
      return 'Téléchargement individuel des photos (optimisé pour mobile)';
    case 'sequential':
      return 'Téléchargement séquentiel (compatible avec tous les navigateurs)';
    default:
      return 'Téléchargement optimisé pour votre appareil';
  }
}